// src/pages/PlacemakersDataPage.js
import React, { useState } from 'react';
import { useRegion } from '../../context/RegionContext';
import api from '../../services/api';
import './PlacemakersDataPage.css'; 

function PlacemakersDataPage() {
  const { selectedRegion } = useRegion();
  
  const [file, setFile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedUpdates, setSelectedUpdates] = useState(new Set());
  const [materialFactors, setMaterialFactors] = useState({}); // ✅ NEW: Store factors for each material
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);

  // ✅ Check if user is in NZ region
  const isNZRegion = selectedRegion === 'vquest';

  // ✅ NEW: Auto-detect conversion factor from product name
  const detectFactor = (name) => {
    if (!name) return 1;
    
    // Match single length dimension: "x 3.6m", "x 2.4m", etc.
    const lengthMatch = name.match(/x\s+(\d+\.?\d*)\s*m\s*$/i);
    if (lengthMatch) {
      return parseFloat(lengthMatch[1]);
    }
    
    // Match area dimensions: "2.4m x 1.2m", "2400mm x 1200mm"
    const areaMatchM = name.match(/(\d+\.?\d*)\s*m\s*x\s*(\d+\.?\d*)\s*m/i);
    if (areaMatchM) {
      return parseFloat(areaMatchM[1]) * parseFloat(areaMatchM[2]);
    }
    
    // Match area in mm (convert to m²): "2400mm x 1200mm"
    const areaMatchMM = name.match(/(\d+)\s*mm\s*x\s*(\d+)\s*mm/i);
    if (areaMatchMM) {
      const lengthM = parseFloat(areaMatchMM[1]) / 1000;
      const widthM = parseFloat(areaMatchMM[2]) / 1000;
      return lengthM * widthM;
    }
    
    // Default: no conversion needed
    return 1;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setSuccess('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    if (!isNZRegion) {
      setError('Placemakers updates are only available for New Zealand region');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setMatches([]);
    setSelectedUpdates(new Set());
    setStats(null);
    setMaterialFactors({}); // ✅ Reset factors

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/placemakers/parse-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setMatches(response.data.matches);
        
        // ✅ NEW: Auto-detect factors for all matched materials
        const detectedFactors = {};
        response.data.matches.forEach(material => {
          detectedFactors[material.measurementUnitRateId] = detectFactor(material.name);
        });
        setMaterialFactors(detectedFactors);
        
        setStats({
          totalRows: response.data.totalRows,
          matchedRows: response.data.matchedRows,
          unmatchedCount: response.data.unmatchedCount
        });
        setSuccess(`Found ${response.data.matchedRows} matching materials out of ${response.data.totalRows} CSV rows`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload CSV file');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Update factor for specific material
  const updateFactor = (measurementUnitRateId, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) return; // Prevent invalid factors
    
    setMaterialFactors(prev => ({
      ...prev,
      [measurementUnitRateId]: numValue
    }));
  };

  const toggleSelection = (measurementUnitRateId) => {
    setSelectedUpdates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(measurementUnitRateId)) {
        newSet.delete(measurementUnitRateId);
      } else {
        newSet.add(measurementUnitRateId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUpdates.size === matches.length) {
      setSelectedUpdates(new Set());
    } else {
      setSelectedUpdates(new Set(matches.map(m => m.measurementUnitRateId)));
    }
  };

  const handleUpdate = async () => {
    if (selectedUpdates.size === 0) {
      setError('Please select at least one item to update');
      return;
    }

    if (!window.confirm(`Are you sure you want to update ${selectedUpdates.size} material(s)?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // ✅ UPDATED: Include factor in updates
      const updates = matches
        .filter(m => selectedUpdates.has(m.measurementUnitRateId))
        .map(m => {
          const factor = materialFactors[m.measurementUnitRateId] || 1;
          const adjustedValue = parseFloat(m.newValue) / factor;
          
          return {
            measurementUnitRateId: m.measurementUnitRateId,
            newValue: adjustedValue, // ✅ Send adjusted value
            measurementId: m.measurementId,
            factor: factor, // ✅ Include factor for storage
            rawValue: m.newValue // ✅ Original CSV value for reference
          };
        });

      const response = await api.post('/placemakers/update-prices', { updates });

      if (response.data.success) {
        setSuccess(`Successfully updated ${response.data.updatedCount} materials`);
        setMatches([]);
        setSelectedUpdates(new Set());
        setMaterialFactors({});
        setFile(null);
        setStats(null);

        // ✅ Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.response?.data?.error || 'Failed to update materials');
    } finally {
      setLoading(false);
    }
  };

  if (!isNZRegion) {
    return (
      <div className="placemakers-container">
        <div className="page-title-section">
          <h1>Placemakers Price Updates</h1>
          <div className="region-indicator-under-title">
            🌏 Current Region: <strong>{selectedRegion}</strong>
          </div>
        </div>

        <div className="access-denied">
          <div className="access-denied-icon">🚫</div>
          <h2>Access Restricted</h2>
          <p>Placemakers price updates are only available for the <strong>New Zealand</strong> region.</p>
          <p>Your current region: <strong>{selectedRegion}</strong></p>
          <p>Please switch to New Zealand region to access this feature.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="placemakers-container">
      <div className="page-title-section">
        <h1>Placemakers Price Updates</h1>
        <div className="region-indicator-under-title">
          🌏 Current Region: <strong>New Zealand</strong>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span className="alert__icon">⚠️</span>
          <span className="alert__text">{error}</span>
          <button className="alert__close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert--success">
          <span className="alert__icon">✅</span>
          <span className="alert__text">{success}</span>
          <button className="alert__close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="upload-section">
        <h2>Upload Placemakers CSV</h2>
        <p className="section-description">
          Upload a CSV file with SKU (Column A) and Value (Column G) to update existing Placemakers materials
        </p>

        <div className="file-input-group">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={loading}
            className="file-input"
          />
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className={`btn btn--primary ${loading ? 'btn--loading' : ''}`}
          >
            {loading ? 'Processing...' : 'Parse CSV'}
          </button>
        </div>

        {stats && (
          <div className="stats-box">
            <div className="stat-item">
              <span className="stat-label">Total Rows:</span>
              <span className="stat-value">{stats.totalRows}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Matched:</span>
              <span className="stat-value stat-value--success">{stats.matchedRows}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Unmatched:</span>
              <span className="stat-value stat-value--warning">{stats.unmatchedCount}</span>
            </div>
          </div>
        )}
      </div>

      {matches.length > 0 && (
        <div className="results-section">
          {/* ✅ NEW: Factor Help Section */}
          <div className="factor-help">
            <h4>📏 Unit Conversion Guide</h4>
            <p>
              Some materials may have different units in the CSV. 
              The <strong>Factor</strong> column has been auto-detected where possible. Adjust if needed:
            </p>
            <ul>
              <li><strong>Length products</strong> (e.g., "x 3.6m"): Factor = 3.6 → converts per-length to per-meter</li>
              <li><strong>Sheet products</strong> (e.g., "2.4m x 1.2m"): Factor = 2.88 → converts per-sheet to per-m²</li>
              <li><strong>Matching units</strong>: Factor = 1 (no conversion needed)</li>
            </ul>
            <p className="factor-formula">
              <strong>Formula:</strong> Adjusted Price = CSV Price ÷ Factor
            </p>
          </div>

          <div className="results-header">
            <h2>Found {matches.length} Matching Materials</h2>
            <div className="results-actions">
              <button
                onClick={toggleSelectAll}
                className="btn btn--secondary btn--small"
              >
                {selectedUpdates.size === matches.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleUpdate}
                disabled={selectedUpdates.size === 0 || loading}
                className="btn btn--success"
              >
                Update Selected ({selectedUpdates.size})
              </button>
            </div>
          </div>

          <div className="table-container">
            <table className="placemakers-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedUpdates.size === matches.length && matches.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>CSV Price</th>
                  <th>Factor</th>
                  <th>Adjusted Price</th>
                  <th>Current Price</th>
                  <th>Change</th>
                  <th>Change %</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => {
                  const isSelected = selectedUpdates.has(match.measurementUnitRateId);
                  
                  // ✅ Parse values as numbers
                  const currentValue = parseFloat(match.currentValue) || 0;
                  const csvValue = parseFloat(match.newValue) || 0;
                  const factor = materialFactors[match.measurementUnitRateId] || 1;
                  const adjustedValue = csvValue / factor;
                  
                  // ✅ Recalculate price change based on adjusted value
                  const priceChange = adjustedValue - currentValue;
                  const priceChangePercent = currentValue !== 0 ? (priceChange / currentValue) * 100 : 0;
                  
                  const priceIncreased = priceChange > 0;
                  const priceDecreased = priceChange < 0;
                  
                  // ✅ Highlight auto-detected factors (not manually edited)
                  const isAutoDetected = factor !== 1 && materialFactors[match.measurementUnitRateId] === detectFactor(match.name);

                  return (
                    <tr key={match.measurementUnitRateId} className={isSelected ? 'row-selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(match.measurementUnitRateId)}
                        />
                      </td>
                      <td className="sku-cell">{match.sku}</td>
                      <td className="name-cell" title={match.name}>{match.name}</td>
                      <td>{match.category || 'N/A'}</td>
                      <td className="price-cell csv-price">${csvValue.toFixed(2)}</td>
                      <td>
                        <input 
                          type="number" 
                          step="0.01"
                          min="0.01"
                          value={factor}
                          onChange={(e) => updateFactor(match.measurementUnitRateId, e.target.value)}
                          className={`factor-input ${isAutoDetected ? 'factor-auto-detected' : ''}`}
                          title={isAutoDetected ? 'Auto-detected factor' : 'Manual factor'}
                        />
                      </td>
                      <td className="price-cell price-adjusted">
                        ${adjustedValue.toFixed(2)}
                        {factor !== 1 && (
                          <span className="factor-indicator" title={`${csvValue.toFixed(2)} ÷ ${factor}`}>
                            ÷{factor}
                          </span>
                        )}
                      </td>
                      <td className="price-cell">${currentValue.toFixed(2)}</td>
                      <td className={`price-change ${priceIncreased ? 'price-increase' : priceDecreased ? 'price-decrease' : ''}`}>
                        {priceIncreased && '↑ '}
                        {priceDecreased && '↓ '}
                        ${Math.abs(priceChange).toFixed(2)}
                      </td>
                      <td className={`price-change ${priceIncreased ? 'price-increase' : priceDecreased ? 'price-decrease' : ''}`}>
                        {priceIncreased && '↑ '}
                        {priceDecreased && '↓ '}
                        {Math.abs(priceChangePercent).toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlacemakersDataPage;
